import { ATTENDEE_EMAIL_SHAPE, formatDateRange, formatPackagePriceLabel } from '@longy/common'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { api, ClinicRouterOutput, getTRPCErrorMessage, isTRPCClientError } from '../../core/trpc'
import { useAuth } from '../../hooks/useAuth'
import { useLocale } from '../../hooks/useLocale'
import {
    buildCheckoutBillingDefaults,
    buildPersonalBilling,
    toProfileUpdate,
} from '../../utils/billingDefaults'
import { isFreeRegistration } from '../../utils/freeCheckout'
import { compactRichTextClassName } from '../../utils/richText'
import { AttachedBlocks } from '../cms/AttachedBlocks'
import { EarlyBirdPrice } from '../events/EarlyBirdPrice'
import { TaxBreakdown } from './TaxBreakdown'

type CheckoutItems = ClinicRouterOutput['webCheckout']['getCheckoutItems']

type CheckoutFormValues = {
    selectedItem: string
    selectedEventPackageId: string
    quantity: number
    attendeeNames: { value: string }[]
    attendeeEmails: { value: string }[]
    firstName: string
    lastName: string
    email: string
    phone: string
    isCompany: boolean
    billingName: string
    billingPostalCode: string
    billingCity: string
    billingStreet: string
    billingTaxNumber: string
    acceptsTerms: boolean
    acceptsPaymentObligation: boolean
}

const formatPrice = (price: number) => {
    return (
        new Intl.NumberFormat('hu-HU', {
            style: 'decimal',
            maximumFractionDigits: 0,
        }).format(price) + ' Ft'
    )
}

const useFreeAwarePriceFormat = () => {
    const { t } = useTranslation()
    return (price: number) => (price === 0 ? t('common.ui.freeOfCharge') : formatPrice(price))
}

const inputClassName =
    'w-full rounded-2xl border border-slate-200 bg-white px-6 py-4 text-brand-dark shadow-sm transition-all placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary'

type Props = {
    data: CheckoutItems
    defaultProgramId?: string
    defaultEventId?: string
    defaultEventPackageId?: string
}

export const CheckoutForm = ({
    data,
    defaultProgramId,
    defaultEventId,
    defaultEventPackageId,
}: Props) => {
    const { getLocalizedValue } = useLocale()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const formatPriceOrFree = useFreeAwarePriceFormat()
    const { mutateAsync, isPending } = api.webCheckout.initCheckout.useMutation()
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [step, setStep] = useState<'form' | 'summary'>('form')
    const [isRedirecting, setIsRedirecting] = useState(false)
    const { user, utils } = useAuth()
    const { mutateAsync: saveProfile } = api.user.editSelf.useMutation()
    const [sameAsPersonal, setSameAsPersonal] = useState(true)
    const [saveToProfile, setSaveToProfile] = useState(false)

    const defaultSelected = defaultProgramId
        ? `program:${defaultProgramId}`
        : defaultEventId
          ? `event:${defaultEventId}`
          : ''

    const {
        register,
        handleSubmit,
        watch,
        trigger,
        getValues,
        setValue,
        reset,
        formState: { errors },
    } = useForm<CheckoutFormValues>({
        defaultValues: {
            selectedItem: defaultSelected,
            selectedEventPackageId: defaultEventPackageId ?? '',
            quantity: 1,
            attendeeNames: [{ value: '' }],
            attendeeEmails: [{ value: '' }],
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            isCompany: false,
            billingName: '',
            billingPostalCode: '',
            billingCity: '',
            billingStreet: '',
            billingTaxNumber: '',
            acceptsTerms: false,
            acceptsPaymentObligation: false,
        },
    })

    const selectedItem = watch('selectedItem')
    const selectedEventPackageId = watch('selectedEventPackageId')
    const quantity = watch('quantity')
    const attendeeNames = watch('attendeeNames')
    const attendeeEmails = watch('attendeeEmails')
    const isCompany = watch('isCompany')
    const buyerName = `${watch('lastName') ?? ''} ${watch('firstName') ?? ''}`.trim()

    const personalBilling = useMemo(() => buildPersonalBilling(user), [user])
    const prefilled = useRef(false)

    useEffect(() => {
        if (prefilled.current || !user) return

        prefilled.current = true
        const billing = buildCheckoutBillingDefaults(user)
        reset({
            ...getValues(),
            ...billing.values,
            firstName: user.firstName ?? '',
            lastName: user.lastName ?? '',
            email: user.email ?? '',
            isCompany: !!billing.values.billingTaxNumber,
        })
        setSameAsPersonal(billing.sameAsPersonal)
    }, [user, reset, getValues])

    const mirrorsPersonal = !!user && sameAsPersonal && !isCompany

    useEffect(() => {
        if (!mirrorsPersonal) return

        setValue('billingName', buyerName)
        if (personalBilling.billingPostalCode) {
            setValue('billingPostalCode', personalBilling.billingPostalCode)
        }
        if (personalBilling.billingCity) setValue('billingCity', personalBilling.billingCity)
        if (personalBilling.billingStreet) setValue('billingStreet', personalBilling.billingStreet)
    }, [mirrorsPersonal, buyerName, personalBilling, setValue])

    const isMirrored = (field: 'billingPostalCode' | 'billingCity' | 'billingStreet') =>
        mirrorsPersonal && !!personalBilling[field]

    const selectedItemType = selectedItem?.startsWith('program:')
        ? 'program'
        : selectedItem?.startsWith('event:')
          ? 'event'
          : undefined
    const selectedItemId = selectedItem?.split(':')[1] ?? ''

    const selectedProgram =
        selectedItemType === 'program' ? data.programs.find((p) => p.id === selectedItemId) : null

    const selectedEvent =
        selectedItemType === 'event' ? data.events.find((e) => e.id === selectedItemId) : null

    const eventPackages = selectedEvent?.packages ?? []
    const hasEventPackages = eventPackages.length > 0
    const mustPickEventPackage = hasEventPackages && !selectedEventPackageId
    const selectedEventPackage = eventPackages.find((p) => p.id === selectedEventPackageId)

    useEffect(() => {
        if (selectedItemType !== 'event') {
            if (selectedEventPackageId) setValue('selectedEventPackageId', '')
            return
        }
        if (!selectedEvent) return
        if (selectedEvent.packages.length === 1) {
            if (selectedEventPackageId !== selectedEvent.packages[0].id) {
                setValue('selectedEventPackageId', selectedEvent.packages[0].id)
            }
            return
        }
        if (
            selectedEventPackageId &&
            !selectedEvent.packages.some((p) => p.id === selectedEventPackageId)
        ) {
            setValue('selectedEventPackageId', '')
        }
    }, [selectedItemType, selectedEvent, selectedEventPackageId, setValue])

    const eventUnitPrice = selectedEventPackage?.price ?? selectedEvent?.price ?? 0
    const unitPrice = selectedProgram?.price ?? eventUnitPrice
    // The buyer can never ask for more places than the event has left; a lifestyle program is a
    // package rather than a seat, so nothing caps it.
    const maxQuantity =
        selectedItemType === 'event' ? Math.max(selectedEvent?.availableSpaces ?? 1, 1) : undefined
    const billedQuantity = Math.min(
        Math.max(quantity || 1, 1),
        maxQuantity ?? Number.MAX_SAFE_INTEGER,
    )
    const totalPrice = unitPrice * billedQuantity
    const tax = selectedProgram?.tax ?? selectedEvent?.tax
    const originalUnitPrice =
        selectedProgram?.originalPrice ?? selectedEventPackage?.originalPrice ?? null
    const originalTotalPrice =
        originalUnitPrice === null ? null : originalUnitPrice * billedQuantity
    const earlyBirdDeadline = selectedEventPackage?.earlyBirdDeadline ?? null

    const isFree = isFreeRegistration({
        hasResolvedPrice:
            Boolean(selectedProgram) || Boolean(selectedEvent && !mustPickEventPackage),
        totalPrice,
    })

    const itemName = selectedProgram?.name
        ? selectedProgram.name
        : selectedEvent?.name
          ? selectedEventPackage
              ? `${getLocalizedValue(selectedEvent.name)} – ${selectedEventPackage.name}`
              : getLocalizedValue(selectedEvent.name)
          : ''

    const collectsAttendeeNames = selectedItemType === 'event'

    const attendeeNameValues = Array.from(
        { length: billedQuantity },
        (_, index) => attendeeNames?.[index]?.value?.trim() || buyerName,
    )

    const attendeeEmailValues = Array.from(
        { length: billedQuantity },
        (_, index) => attendeeEmails?.[index]?.value?.trim() ?? '',
    )

    const watchedValues = watch()
    const pendingProfileUpdate = user
        ? toProfileUpdate(
              {
                  ...watchedValues,
                  billingTaxNumber: watchedValues.isCompany
                      ? watchedValues.billingTaxNumber
                      : undefined,
              },
              user,
              { sameAsPersonal: mirrorsPersonal, includesBilling: !isFree },
          )
        : null

    const handleGoToSummary = async () => {
        const valid = await trigger()
        if (valid) setStep('summary')
    }

    const onSubmit = async (values: CheckoutFormValues) => {
        setSubmitError(null)

        const profileUpdate =
            saveToProfile && user
                ? toProfileUpdate(
                      {
                          ...values,
                          billingTaxNumber: values.isCompany ? values.billingTaxNumber : undefined,
                      },
                      user,
                      { sameAsPersonal: mirrorsPersonal, includesBilling: !isFree },
                  )
                : null

        if (profileUpdate) {
            try {
                await saveProfile(profileUpdate)
                await utils.user.getMe.invalidate()
            } catch (caught) {
                console.warn('Failed to save checkout details to the account', caught)
            }
        }

        try {
            const [type, id] = values.selectedItem.split(':')
            const result = await mutateAsync({
                itemType: type as 'program' | 'event',
                itemId: id as string,
                eventPackageId:
                    type === 'event' && values.selectedEventPackageId
                        ? values.selectedEventPackageId
                        : undefined,
                quantity: billedQuantity,
                attendeeNames: type === 'event' ? attendeeNameValues : undefined,
                attendeeEmails: type === 'event' ? attendeeEmailValues : undefined,
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                phone: values.phone || undefined,
                isCompany: isFree ? false : values.isCompany,
                // A free registration is invoiced to nobody, so it collects none of this. The server
                // re-prices the item and rejects a paid purchase that arrives without it.
                billingName: isFree ? undefined : values.billingName,
                billingPostalCode: isFree ? undefined : values.billingPostalCode,
                billingCity: isFree ? undefined : values.billingCity,
                billingStreet: isFree ? undefined : values.billingStreet,
                billingTaxNumber: !isFree && values.isCompany ? values.billingTaxNumber : undefined,
                acceptsTerms: true,
                acceptsPaymentObligation: isFree ? undefined : true,
            })

            // A free registration is already booked by the time this resolves; there is no payment
            // provider to hand the browser over to.
            if (result.kind === 'free') {
                await navigate({
                    to: '/checkout/success',
                    search: { transactionId: result.transactionId },
                })
                return
            }

            setIsRedirecting(true)
            window.location.href = result.checkoutUrl
            return
        } catch (error) {
            // A refused checkout — a full event above all — has to say what went wrong, not just
            // that something did. A checkout that fails on the server carries no message for the
            // buyer, so those keep a line of their own, one per kind of checkout.
            const isServerFailure =
                !isTRPCClientError(error) || error.data?.code === 'INTERNAL_SERVER_ERROR'

            setSubmitError(
                isServerFailure
                    ? t(
                          isFree
                              ? 'frontend.checkout.registrationError'
                              : 'common.errors.checkoutFailed',
                      )
                    : getTRPCErrorMessage(error),
            )
        }
    }

    if (step === 'summary') {
        const values = getValues()
        return (
            <div className="flex flex-col gap-6 rounded-[2rem] border border-slate-100 bg-white p-10 shadow-xl shadow-slate-100">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-brand-gray transition-colors hover:border-primary hover:text-primary"
                        onClick={() => setStep('form')}
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <h2 className="text-2xl font-bold text-brand-dark">
                        {isFree ? t('frontend.checkout.freeRegistrationTitle') : 'Összegzés'}
                    </h2>
                </div>

                <div className="flex flex-col gap-4">
                    <div className="rounded-2xl bg-primary/5 px-6 py-5">
                        <h3 className="mb-3 text-sm font-semibold text-brand-dark">
                            Kiválasztott tétel
                        </h3>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-semibold text-brand-dark">{itemName}</p>
                                {selectedEvent?.startDate && (
                                    <p className="text-sm text-brand-gray">
                                        {formatDateRange(
                                            selectedEvent.startDate,
                                            selectedEvent.endDate,
                                        )}
                                    </p>
                                )}
                                {billedQuantity > 1 && (
                                    <p className="text-sm text-brand-gray">
                                        {billedQuantity} db × {formatPrice(unitPrice)}
                                    </p>
                                )}
                                {collectsAttendeeNames && (
                                    <p className="text-sm text-brand-gray">
                                        {billedQuantity > 1
                                            ? t('frontend.checkout.attendees')
                                            : t('frontend.checkout.attendee')}
                                        : {attendeeNameValues.join(', ')}
                                    </p>
                                )}
                            </div>
                            {isFree ? (
                                <span className="font-bold text-primary">
                                    {t('common.ui.freeOfCharge')}
                                </span>
                            ) : (
                                <EarlyBirdPrice
                                    align="end"
                                    deadline={earlyBirdDeadline}
                                    format={formatPrice}
                                    originalPrice={originalTotalPrice}
                                    price={totalPrice}
                                />
                            )}
                        </div>
                        <AttachedBlocks
                            compact
                            blocks={selectedProgram?.cmsBlocks}
                            className="mt-3 border-t border-primary/10 pt-3"
                        />
                        {selectedProgram?.description && (
                            <div
                                className={`mt-3 border-t border-primary/10 pt-3 ${compactRichTextClassName}`}
                                dangerouslySetInnerHTML={{ __html: selectedProgram.description }}
                            />
                        )}
                        {!isFree && (
                            <TaxBreakdown
                                tax={tax}
                                totalPrice={totalPrice}
                                className="mt-3 flex flex-col gap-2 border-t border-primary/10 pt-3"
                            />
                        )}
                    </div>

                    <div className="rounded-2xl border border-slate-100 px-6 py-5">
                        <h3 className="mb-3 text-sm font-semibold text-brand-dark">
                            Személyes adatok
                        </h3>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <span className="text-brand-gray">Név:</span>
                            <span className="text-brand-dark">
                                {values.lastName} {values.firstName}
                            </span>
                            <span className="text-brand-gray">E-mail:</span>
                            <span className="text-brand-dark">{values.email}</span>
                            {values.phone && (
                                <>
                                    <span className="text-brand-gray">Telefon:</span>
                                    <span className="text-brand-dark">{values.phone}</span>
                                </>
                            )}
                        </div>
                    </div>

                    {!isFree && (
                        <div className="rounded-2xl border border-slate-100 px-6 py-5">
                            <h3 className="mb-3 text-sm font-semibold text-brand-dark">
                                Számlázási adatok
                            </h3>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <span className="text-brand-gray">
                                    {values.isCompany ? 'Cégnév:' : 'Név:'}
                                </span>
                                <span className="text-brand-dark">{values.billingName}</span>
                                <span className="text-brand-gray">Cím:</span>
                                <span className="text-brand-dark">
                                    {values.billingPostalCode} {values.billingCity},{' '}
                                    {values.billingStreet}
                                </span>
                                {values.isCompany && values.billingTaxNumber && (
                                    <>
                                        <span className="text-brand-gray">Adószám:</span>
                                        <span className="text-brand-dark">
                                            {values.billingTaxNumber}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {submitError && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-4 text-sm text-red-800">
                        {submitError}
                    </div>
                )}

                <button
                    type="button"
                    disabled={isPending || isRedirecting}
                    className="flex items-center justify-center gap-3 rounded-2xl bg-primary px-8 py-5 text-base font-bold text-white shadow-xl shadow-primary/20 transition-all hover:bg-primary/90 disabled:opacity-60"
                    onClick={handleSubmit(onSubmit)}
                >
                    {isPending || isRedirecting ? (
                        <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            {isFree
                                ? t('frontend.checkout.registering')
                                : 'Átirányítás a fizetéshez...'}
                        </>
                    ) : isFree ? (
                        t('frontend.checkout.register')
                    ) : (
                        `Fizetés – ${formatPrice(totalPrice)}`
                    )}
                </button>
            </div>
        )
    }

    return (
        <form
            className="flex flex-col gap-5 rounded-[2rem] border border-slate-100 bg-white p-10 shadow-xl shadow-slate-100"
            onSubmit={(e) => {
                e.preventDefault()
                handleGoToSummary()
            }}
        >
            <h2 className="text-2xl font-bold text-brand-dark">Adatok</h2>

            <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-brand-dark">Csomag / Esemény*</label>
                <select
                    {...register('selectedItem', { required: true })}
                    className={inputClassName + ' appearance-none'}
                >
                    <option value="">Válasszon...</option>
                    {data.programs.length > 0 && (
                        <optgroup label="Lifestyle programok">
                            {data.programs.map((program) => (
                                <option key={program.id} value={`program:${program.id}`}>
                                    {program.name} – {formatPriceOrFree(program.price)}
                                </option>
                            ))}
                        </optgroup>
                    )}
                    {data.events.length > 0 && (
                        <optgroup label="Események">
                            {data.events.map((event) => (
                                <option key={event.id} value={`event:${event.id}`}>
                                    {event.name ? getLocalizedValue(event.name) : 'Esemény'}
                                    {event.startDate &&
                                        ` – ${formatDateRange(event.startDate, event.endDate)}`}
                                    {event.price != null && ` – ${formatPriceOrFree(event.price)}`}
                                </option>
                            ))}
                        </optgroup>
                    )}
                </select>
                {errors.selectedItem && (
                    <span className="text-xs text-red-500">
                        Válasszon egy csomagot vagy eseményt
                    </span>
                )}
            </div>

            {hasEventPackages && eventPackages.length > 1 && (
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-brand-dark">Csomag*</label>
                    <select
                        {...register('selectedEventPackageId', {
                            required: hasEventPackages,
                        })}
                        className={inputClassName + ' appearance-none'}
                    >
                        <option value="">Válassz csomagot...</option>
                        {eventPackages.map((pkg) => (
                            <option key={pkg.id} value={pkg.id}>
                                {pkg.name} –{' '}
                                {formatPackagePriceLabel(
                                    pkg,
                                    formatPriceOrFree,
                                    t('frontend.events.earlyBirdOptionSuffix'),
                                )}
                            </option>
                        ))}
                    </select>
                    {errors.selectedEventPackageId && (
                        <span className="text-xs text-red-500">Válasszon egy csomagot</span>
                    )}
                </div>
            )}

            {(selectedItemType === 'program' || selectedItemType === 'event') && (
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-brand-dark">
                        {t('frontend.checkout.ticketCount')}
                    </label>
                    <input
                        {...register('quantity', {
                            required: true,
                            min: 1,
                            max: maxQuantity,
                            valueAsNumber: true,
                        })}
                        className={inputClassName}
                        type="number"
                        min={1}
                        max={maxQuantity}
                    />
                    {maxQuantity !== undefined && (
                        <span className="text-xs text-brand-gray">
                            {t('frontend.events.freePlaces')}: {maxQuantity}
                        </span>
                    )}
                    {errors.quantity && (
                        <span className="text-xs text-red-500">
                            {t('frontend.checkout.ticketCountInvalid')}
                        </span>
                    )}
                </div>
            )}

            {/* Every ticket is admitted on its own name, so each one is asked for. The first is the
                buyer's, already typed below, which is why only the rest are required. */}
            {collectsAttendeeNames && (
                <div className="flex flex-col gap-3">
                    <label className="text-sm font-semibold text-brand-dark">
                        {billedQuantity > 1
                            ? t('frontend.checkout.attendees')
                            : t('frontend.checkout.attendee')}
                    </label>
                    {/* The address is optional: filling it in sends that one ticket straight to the
                        person it admits, and leaving it blank simply means it is passed on by hand. */}
                    <span className="text-xs text-brand-gray">
                        {t('frontend.checkout.attendeeEmailHint')}
                    </span>
                    {Array.from({ length: billedQuantity }, (_, index) => (
                        <div key={index} className="flex flex-col gap-2">
                            <span className="text-xs text-brand-gray">
                                {billedQuantity > 1
                                    ? t('frontend.checkout.attendeeName', { index: index + 1 })
                                    : t('frontend.checkout.attendee')}
                            </span>
                            <input
                                {...register(`attendeeNames.${index}.value` as const, {
                                    required: index > 0,
                                })}
                                className={inputClassName}
                                placeholder={index === 0 ? buyerName : undefined}
                            />
                            {errors.attendeeNames?.[index]?.value && (
                                <span className="text-xs text-red-500">
                                    {t('frontend.checkout.attendeeNameRequired')}
                                </span>
                            )}
                            <input
                                {...register(`attendeeEmails.${index}.value` as const, {
                                    // The shape the server checks, so the form cannot accept an
                                    // address the purchase would then be refused for.
                                    pattern: ATTENDEE_EMAIL_SHAPE,
                                })}
                                className={inputClassName}
                                type="email"
                                placeholder={t('frontend.checkout.attendeeEmail')}
                            />
                            {errors.attendeeEmails?.[index]?.value && (
                                <span className="text-xs text-red-500">
                                    {t('frontend.checkout.attendeeEmailInvalid')}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {(selectedProgram || (selectedEvent && !mustPickEventPackage)) && (
                <div className="rounded-2xl bg-primary/5 px-6 py-4">
                    <AttachedBlocks compact blocks={selectedProgram?.cmsBlocks} className="mb-3" />
                    {selectedProgram?.description && (
                        <div
                            className={`mb-3 ${compactRichTextClassName}`}
                            dangerouslySetInnerHTML={{
                                __html: selectedProgram.description,
                            }}
                        />
                    )}
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-sm font-semibold text-brand-dark">
                            {isFree ? 'Összesen:' : 'Összesen (bruttó):'}
                        </span>
                        {isFree ? (
                            <span className="font-bold text-primary">
                                {t('common.ui.freeOfCharge')}
                            </span>
                        ) : (
                            <EarlyBirdPrice
                                align="end"
                                deadline={earlyBirdDeadline}
                                format={formatPrice}
                                originalPrice={originalTotalPrice}
                                price={totalPrice}
                            />
                        )}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-brand-dark">Vezetéknév*</label>
                    <input
                        {...register('lastName', { required: true })}
                        className={inputClassName}
                        placeholder="Vezetéknév"
                    />
                    {errors.lastName && (
                        <span className="text-xs text-red-500">Ez a mező kötelező</span>
                    )}
                </div>
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-brand-dark">Keresztnév*</label>
                    <input
                        {...register('firstName', { required: true })}
                        className={inputClassName}
                        placeholder="Keresztnév"
                    />
                    {errors.firstName && (
                        <span className="text-xs text-red-500">Ez a mező kötelező</span>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-brand-dark">E-mail cím*</label>
                <input
                    {...register('email', {
                        required: true,
                        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    })}
                    className={inputClassName}
                    placeholder="pelda@email.com"
                    type="email"
                />
                {errors.email && (
                    <span className="text-xs text-red-500">
                        {errors.email.type === 'pattern'
                            ? 'Érvényes email-t adjon meg'
                            : 'Ez a mező kötelező'}
                    </span>
                )}
            </div>

            <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-brand-dark">Telefonszám</label>
                <input
                    {...register('phone')}
                    className={inputClassName}
                    placeholder="+36 30 123 4567"
                    type="tel"
                />
            </div>

            {/*
             * Billing exists to be put on an invoice, and a 0 Ft registration issues none. The
             * `required` rules are conditional rather than just the markup: react-hook-form keeps
             * unmounted fields registered, so hiding these inputs alone would leave `trigger()`
             * failing on rules for fields the user can no longer see.
             */}
            {!isFree && (
                <>
                    {user && !isCompany && (
                        <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm transition-all hover:border-primary/30">
                            <input
                                type="checkbox"
                                checked={sameAsPersonal}
                                className="h-5 w-5 rounded border-slate-300 text-primary accent-primary"
                                onChange={(event) => setSameAsPersonal(event.target.checked)}
                            />
                            <span className="text-sm font-semibold text-brand-dark">
                                {t('frontend.booking.sameAsPersonal')}
                            </span>
                        </label>
                    )}

                    <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm transition-all hover:border-primary/30">
                        <input
                            {...register('isCompany')}
                            type="checkbox"
                            className="h-5 w-5 rounded border-slate-300 text-primary accent-primary"
                        />
                        <span className="text-sm font-semibold text-brand-dark">
                            Céges számlát kérek
                        </span>
                    </label>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-brand-dark">
                            {isCompany ? 'Cégnév*' : 'Számlázási név*'}
                        </label>
                        <input
                            {...register('billingName', { required: !isFree })}
                            readOnly={mirrorsPersonal}
                            className={inputClassName}
                            placeholder={isCompany ? 'Cégnév' : 'Számlázási név'}
                        />
                        {errors.billingName && (
                            <span className="text-xs text-red-500">Ez a mező kötelező</span>
                        )}
                    </div>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-semibold text-brand-dark">
                                Irányítószám*
                            </label>
                            <input
                                {...register('billingPostalCode', { required: !isFree })}
                                readOnly={isMirrored('billingPostalCode')}
                                className={inputClassName}
                                placeholder="1234"
                            />
                            {errors.billingPostalCode && (
                                <span className="text-xs text-red-500">Kötelező</span>
                            )}
                        </div>
                        <div className="flex flex-col gap-2 sm:col-span-2">
                            <label className="text-sm font-semibold text-brand-dark">Város*</label>
                            <input
                                {...register('billingCity', { required: !isFree })}
                                readOnly={isMirrored('billingCity')}
                                className={inputClassName}
                                placeholder="Budapest"
                            />
                            {errors.billingCity && (
                                <span className="text-xs text-red-500">Kötelező</span>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-brand-dark">
                            Utca, házszám*
                        </label>
                        <input
                            {...register('billingStreet', { required: !isFree })}
                            readOnly={isMirrored('billingStreet')}
                            className={inputClassName}
                            placeholder="Példa utca 1."
                        />
                        {errors.billingStreet && (
                            <span className="text-xs text-red-500">Ez a mező kötelező</span>
                        )}
                    </div>

                    {isCompany && (
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-semibold text-brand-dark">
                                Adószám*
                            </label>
                            <input
                                {...register('billingTaxNumber', { required: isCompany })}
                                className={inputClassName}
                                placeholder="12345678-1-23"
                            />
                            {errors.billingTaxNumber && (
                                <span className="text-xs text-red-500">
                                    Céges számla esetén kötelező
                                </span>
                            )}
                        </div>
                    )}
                </>
            )}

            {pendingProfileUpdate && (
                <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm transition-all hover:border-primary/30">
                    <input
                        type="checkbox"
                        checked={saveToProfile}
                        className="h-5 w-5 rounded border-slate-300 text-primary accent-primary"
                        onChange={(event) => setSaveToProfile(event.target.checked)}
                    />
                    <span className="text-sm font-semibold text-brand-dark">
                        {t('frontend.booking.saveToProfile')}
                    </span>
                </label>
            )}

            <div className="flex flex-col gap-2">
                <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm transition-all hover:border-primary/30">
                    <input
                        {...register('acceptsTerms', { required: true })}
                        type="checkbox"
                        className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-primary accent-primary"
                    />
                    <span className="text-sm text-brand-dark">
                        Kijelentem, hogy az{' '}
                        <a
                            href="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-primary hover:underline"
                        >
                            Adatvédelmi tájékoztatót
                        </a>{' '}
                        elolvastam, megértettem és elfogadom.*
                    </span>
                </label>
                {errors.acceptsTerms && (
                    <span className="text-xs text-red-500">Ezt a mezőt be kell jelölnie</span>
                )}
            </div>

            {/* Nothing is owed for a free registration, so there is no obligation to consent to. */}
            {!isFree && (
                <div className="flex flex-col gap-2">
                    <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-4 shadow-sm transition-all hover:border-primary/30">
                        <input
                            {...register('acceptsPaymentObligation', { required: !isFree })}
                            type="checkbox"
                            className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-primary accent-primary"
                        />
                        <span className="text-sm text-brand-dark">
                            Kijelentem, hogy tudomásul veszem és elfogadom, hogy a rendelés fizetési
                            kötelezettséggel jár.*
                        </span>
                    </label>
                    {errors.acceptsPaymentObligation && (
                        <span className="text-xs text-red-500">Ezt a mezőt be kell jelölnie</span>
                    )}
                </div>
            )}

            <button
                type="submit"
                disabled={!selectedItem || mustPickEventPackage}
                className="mt-2 flex items-center justify-center gap-3 rounded-2xl bg-primary px-8 py-5 text-base font-bold text-white shadow-xl shadow-primary/20 transition-all hover:bg-primary/90 disabled:opacity-60"
            >
                Tovább az összegzéshez
            </button>
        </form>
    )
}
